/**
 * The Results headline in three sentences (screens §P9.1): where you stand against your twin, what the year did to the value of a share for you and for your twin,
 * and the one step that explains the gap. Pure; every figure is a report field (`score`, `scorecard.components.priceMoves`, `scorecard.valueBridge`).
 */
import type { ContentCatalog, RoundReport } from "@bsim/contracts";
import type { GlossaryTerm, ValueBridgeStep } from "@bsim/domain";
import { formatPointsWord, formatUsdAShare, formatUsdPerShare } from "../format";
import { TIER_LABELS, printedParts } from "../score/labels";
import { stepLabel } from "./derive";
import { lowerFirst } from "../format/labels";

/** Under half a cent a figure reads as no move. */
const NO_MOVE_USD = 0.005;
/** Under ten cents apart, the preview and the played year are one figure for a player. */
export const NEAR_PREVIEW_USD = 0.1;

/** "Behind your twin by $2.46 a share (4.30 points of your twin's value per share)." / "Ahead of your twin by …"; level when the score is zero. */
export function standingSentence(score: Pick<RoundReport["score"], "valueCreatedUsdPerShare" | "valueCreatedRate">): string {
  const usd = score.valueCreatedUsdPerShare;
  if (Math.abs(usd) < NO_MOVE_USD) return "Level with your twin.";
  return `${usd < 0 ? "Behind" : "Ahead of"} your twin by ${formatUsdAShare(Math.abs(usd))} (${formatPointsWord(Math.abs(score.valueCreatedRate), { dp: 2 })} of your twin's value per share).`;
}

/** "rose $1.60" / "fell $0.50" / "did not change", from the sign. */
const valueVerb = (usdPerShare: number): string =>
  Math.abs(usdPerShare) < NO_MOVE_USD ? "did not change" : `${usdPerShare > 0 ? "rose" : "fell"} ${formatUsdPerShare(Math.abs(usdPerShare))}`;

/** "Your share price rose $1.60 this year, counted in today's dollars on your twin's share count. Your twin's share price rose $3.08 on the same basis." Null when the report did not keep the prior prices. */
export function moveSentence(components: RoundReport["score"]["components"]): string | null {
  const m = components?.priceMoves;
  return m === undefined ? null : `Your share price ${valueVerb(m.youUsdPerShare)} this year, counted in today's dollars on your twin's share count. Your twin's share price ${valueVerb(m.twinUsdPerShare)} on the same basis.`;
}

/**
 * The two comparators on one line: the preview the plan was signed on set it against changing nothing on the published forecast (`signedPreview.valueCreatedUsdPerShare`, the figure the Decide page showed);
 * the headline sets the year as it was played against the twin. "Your preview expected +$0.21 on the published forecast; the year turned that into −$0.09." Null when the round was not signed on a preview.
 * What turned one into the other is the waterfall's biggest bar, said once by the hero after this sentence, never twice.
 */
export function previewVersusYearSentence(report: Pick<RoundReport, "signedPreview"> & { readonly score?: Pick<RoundReport["score"], "valueCreatedUsdPerShare"> }): string | null {
  const signed = report.signedPreview?.valueCreatedUsdPerShare;
  if (signed === undefined) return null;
  const played = report.score?.valueCreatedUsdPerShare;
  const expected = `Your preview expected ${formatUsdPerShare(signed, { signed: true })} on the published forecast`;
  if (played === undefined) return `${expected}.`;
  // Under ten cents apart the two are one figure for a player: no second number, no legs in brackets.
  if (Math.abs(played - signed) < NEAR_PREVIEW_USD) return `The year came in as the preview expected (${formatUsdPerShare(played, { signed: true })}).`;
  return `${expected}; the year turned that into ${formatUsdPerShare(played, { signed: true })}.`;
}

/** What each bridge step is, in a clause that follows "Main reason:". The sign decides below/above for the earnings step. */
export function reasonLabel(step: ValueBridgeStep, glossary: readonly GlossaryTerm[] = []): string {
  switch (step.step) {
    case "coreEarnings": return `earnings power ${step.usdPerShare < 0 ? "below" : "above"} your twin's`;
    case "requiredCapitalGrowth": return "the capital your growth required";
    case "excessShortfallAndDistributions": return "cash returned and capital held";
    case "distributions": return "dividends and buybacks paid";
    case "sharesRetired": return "shares retired";
    case "marks": return "bond marks";
    case "coePremium": return "the market's risk premium on your bank";
    case "growth": return "growth beyond the plan";
    default: return lowerFirst(stepLabel(step, glossary));
  }
}

/** The step with the largest dollars a share, either sign; null for an empty bridge. */
export const largestStep = (bridge: readonly ValueBridgeStep[]): ValueBridgeStep | null =>
  bridge.reduce<ValueBridgeStep | null>((best, s) => (best === null || Math.abs(s.usdPerShare) > Math.abs(best.usdPerShare) ? s : best), null);

/** "two more years": the look-through years the score counts beyond the year-end, from the report's own horizon. */
const WORDS = ["no", "one", "two", "three", "four", "five", "six"] as const;
export function moreYears(report: RoundReport): string {
  const years = Math.round((report.lookThrough?.seat.shadowMonths ?? 24) / 12);
  return years <= 0 ? "more years" : `${WORDS[years] ?? String(years)} more ${years === 1 ? "year" : "years"}`;
}

/** What the headline's share-price part is measured on: the one basis the score uses, said wherever that part is printed. */
export const PRICE_PART_BASIS = "vs your twin, since the game opened, in today's dollars";

/**
 * The score's three parts under the scoreboard's labels and cents (`printedParts`, rounded once so they add to the headline): "Against your twin: share price +$1.10 (vs your twin, since the game opened, in today's dollars),
 * cash returned −$0.53, look-through revaluation −$3.39, together −$2.82 a share." Null in results stored before the split.
 */
export function scoreSplitSentence(score: RoundReport["score"]): string | null {
  const c = score.components;
  if (c === undefined || ![c.priceVsTwinUsdPerShare, c.capitalReturnedVsTwinUsdPerShare, c.lookThroughAdjustmentUsdPerShare].every(Number.isFinite)) return null;
  const p = printedParts(c, score.valueCreatedUsdPerShare);
  const money = (v: number): string => formatUsdPerShare(v, { signed: true });
  return `Against your twin: share price ${money(p.price)} (${PRICE_PART_BASIS}), cash returned ${money(p.cash)}, look-through revaluation ${money(p.lookThrough)}, together ${money(score.valueCreatedUsdPerShare)} a share.`;
}

/** The year's reason in the scoreboard's own parts (the bridge's steps are a different basis and are not mixed in). Null before the split. */
export function reasonSentence(report: RoundReport, _glossary: readonly GlossaryTerm[] = []): string | null {
  return scoreSplitSentence(report.score);
}

/** "Solid from 0, Well ahead from +1.75 points, Strong from +3.00 points": the tiers in order, from the catalog's floors; null when it has none. */
export function tierLadderPoints(catalog: Pick<ContentCatalog, "tierFloorsRate">): string | null {
  const floors = catalog.tierFloorsRate;
  if (floors === undefined) return null;
  return (["steward", "topQuartile", "strong"] as const).map((t) => `${TIER_LABELS[t]} from ${floors[t] <= 0 ? "0" : formatPointsWord(floors[t], { signed: true, dp: 2 })}`).join(", ");
}
